import React, { FC, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Box,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip,
    Link,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import EventImageItem from 'components/shared/order-events-timeline/order-events/event-files/event-image-item.component';
import EventFileItem from 'components/shared/order-events-timeline/order-events/event-files/event-file-item.component';
import { useTranslation } from 'react-i18next';
import { File as DenthubFile } from 'models/file';
import { Order } from 'models/order';
import BoxWithHighLighting from 'components/with-hith-light-components/box-with-highlighting.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';

type FileUploadedTableProps = {
    versionCheck?: boolean;
    onDeleteFile: (fileId: string) => void;
    onImageClick: (fileUrl: string) => void;
    onEditFile?: (file: DenthubFile) => void;
    files: DenthubFile[];
    showHightLight?: boolean;
    originalOrder?: Order;
    hideDelete?: boolean;
};

const FileUploadedTable: FC<FileUploadedTableProps> = ({
    versionCheck,
    onDeleteFile,
    onImageClick,
    onEditFile,
    files,
    showHightLight,
    originalOrder,
    hideDelete,
}: FileUploadedTableProps) => {
    const { t: translate } = useTranslation('common');
    const [deleteFileConfirmDialogOpen, setDeleteFileConfirmDialogOpen] = useState(false);
    const [fileId, setFileId] = useState('');

    const isImage = (file: DenthubFile): boolean => {
        return /image/i.test(file.mimetype);
    };

    const getOriginalOrderFileName = (fileId: string) => {
        const file = originalOrder?.filesUrl?.find((item) => item._id === fileId);
        return file ? file.filename : '';
    };
    const handleCloseDialog = () => {
        setDeleteFileConfirmDialogOpen(false);
        setFileId('');
    };
    const handleDeleteClick = (fileId: string) => {
        setFileId(fileId as string);
        setDeleteFileConfirmDialogOpen(true);
    };
    const handleConfirm = () => {
        onDeleteFile(fileId as string);
        setDeleteFileConfirmDialogOpen(false);
        setFileId('');
    };

    return (
        <Box mb={1}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('common:FILE_IMAGE')}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{translate('common:FILENAME')}</TableCell>
                            <TableCell sx={{ width: '200px' }}>{translate('common:UPLOADED_BY')}</TableCell>
                            <TableCell sx={{ width: '150px' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {files.map((file: DenthubFile, index: number) => (
                            <TableRow key={file.url}>
                                <TableCell sx={{ maxWidth: 150 }}>
                                    {isImage(file) ? (
                                        <Box onClick={() => onImageClick(file.url)}>
                                            <EventImageItem fileId={file._id} url={file.url} />
                                        </Box>
                                    ) : (
                                        <EventFileItem url={file.url} />
                                    )}
                                </TableCell>
                                {showHightLight ? (
                                    <TableCell>
                                        <Link href={file.url} target="_blank" underline="none">
                                            <BoxWithHighLighting
                                                originalData={getOriginalOrderFileName(file._id)}
                                                data={file.filename}
                                            />
                                        </Link>
                                    </TableCell>
                                ) : (
                                    <TableCell>{file.filename}</TableCell>
                                )}
                                <TableCell>
                                    <Box>
                                        {file?.created?.on !== 0 ? formatDate(new Date(file.created?.on), 'date') : '-'}
                                    </Box>
                                    <Box>{file.created?.by?._id ? file.created.by.fullName : '-'}</Box>
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                                        {showHightLight &&
                                        isImage(file) &&
                                        (versionCheck || versionCheck === undefined) ? (
                                            <Box>
                                                <Tooltip title={translate('orders:ANNOTATION_INSTRUCTIONS').toString()}>
                                                    <IconButton
                                                        onClick={() => (onEditFile ? onEditFile(file) : null)}
                                                        data-test={`file-uploaded-table.edit-button-${index}`}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        ) : undefined}

                                        {!hideDelete && (versionCheck || versionCheck === undefined) ? (
                                            <Box>
                                                <IconButton
                                                    onClick={() => handleDeleteClick(file._id as string)}
                                                    data-test={`file-uploaded-table.delete-button-${index}`}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        ) : undefined}

                                        <Box>
                                            <Tooltip title={translate('common:DOWNLOAD').toString()}>
                                                <IconButton
                                                    color="primary"
                                                    href={file.url}
                                                    target="_blank"
                                                    data-test={`file-uploaded-table.download-button-${index}`}
                                                >
                                                    <FileDownloadIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ConfirmDialog
                open={deleteFileConfirmDialogOpen || false}
                handleClose={handleCloseDialog}
                dialogTitle={translate('common:DELETE_FILE')}
                dialogContent={translate('common:CONFIRM_DELETE_FILE')}
                handleConfirm={handleConfirm}
            />
        </Box>
    );
};

export default FileUploadedTable;
